import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useCategories } from "../context/CategoriesContext";
import { CITIES } from "../mock/mockData";
import { districtsOf } from "../lib/districts";
import { formatPhone } from "../lib/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", category: "", city: "", district: "", phone: "", password: "" };

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-[#2A2224]">{label}</Label>{children}</div>;

const Pick = ({ value, onChange, placeholder, items, testid, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testid} className="border-[#F0DDE0]"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-64">{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
  </Select>
);

export const AdminAddFirm = ({ headers, toast }) => {
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/firms/create`, f, { headers });
      toast({ title: "İşletme eklendi", description: "Şimdi fotoğraf, açıklama ve diğer bilgileri doldurabilirsiniz." });
      setOpen(false); setF(EMPTY);
      navigate(`/patron/firma/${r.data.id}`);
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "İşletme eklenemedi." });
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="admin-add-firm-btn"
        className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform">
        <Plus className="w-4 h-4" /> İşletme Ekle
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-add-firm-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Yeni İşletme Ekle</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Field label="Firma Adı *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="add-firm-name" /></Field></div>
            <div className="sm:col-span-2"><Field label="Kategori *"><Pick value={f.category} onChange={(v) => set("category", v)} placeholder="Kategori seçin" testid="add-firm-category" items={categories.map((c) => ({ value: c.slug, label: c.name }))} /></Field></div>
            <Field label="Şehir *"><Pick value={f.city} onChange={(v) => setF((p) => ({ ...p, city: v, district: "" }))} placeholder="Şehir seçin" testid="add-firm-city" items={CITIES.map((c) => ({ value: c, label: c }))} /></Field>
            <Field label="İlçe"><Pick value={f.district} onChange={(v) => set("district", v)} placeholder={f.city ? "İlçe seçin" : "Önce şehir"} disabled={!f.city} testid="add-firm-district" items={districtsOf(f.city).map((d) => ({ value: d, label: d }))} /></Field>
            <Field label="Telefon"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} onBlur={() => set("phone", formatPhone(f.phone))} placeholder="05xx xxx xx xx" data-testid="add-firm-phone" /></Field>
            <Field label="Panel Şifresi (isteğe bağlı)"><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="En az 6 karakter" data-testid="add-firm-password" /></Field>
          </div>
          <p className="text-xs text-[#7C6E70]">İşletme hemen yayına girer. Kaydettikten sonra fotoğraf, açıklama ve fiyat gibi bilgileri doldurabilirsiniz.</p>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 h-10 rounded-xl border border-[#F0DDE0] text-sm" data-testid="add-firm-cancel">Vazgeç</button>
            <button onClick={save} disabled={busy || !f.name.trim() || !f.category || !f.city} data-testid="add-firm-submit"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Kaydet ve Düzenle
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
